import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react'
import type { AppSandbox, ThemeTokens } from '@cogo/react-renderer-v2'
import { getIsolatedAppStyle } from '@cogo/react-renderer'

export type StudioPreviewTokens = ThemeTokens | null | undefined

interface UseAppDesignSandboxOptions {
  appId: string | null | undefined
  tokens: StudioPreviewTokens
  colorScheme: 'light' | 'dark'
  containerRef: RefObject<HTMLElement | null>
  apiBaseUrl?: string
}

interface UseAppDesignSandboxResult {
  sandbox: AppSandbox | null
  ready: boolean
}

export function getStudioPreviewSandboxAppId(appId: string): string {
  const stableId = appId.replace(/[^a-zA-Z0-9_.:-]/g, '-').slice(0, 96) || 'generated-app'
  return `studio-preview-${stableId}`
}

export function getStudioPreviewAppId(
  manifest: unknown,
  currentProjectId: string | null | undefined,
): string {
  const record = manifest && typeof manifest === 'object' && !Array.isArray(manifest)
    ? manifest as Record<string, unknown>
    : undefined
  const manifestAppId = typeof record?.appId === 'string' ? record.appId.trim() : ''
  const manifestName = typeof record?.name === 'string' ? record.name.trim() : ''
  return manifestAppId || manifestName || currentProjectId || 'generated-app'
}

export function getStudioPreviewRuntimeScopeAppId(
  manifest: unknown,
  currentProjectId: string | null | undefined,
): string {
  return getStudioPreviewSandboxAppId(getStudioPreviewAppId(manifest, currentProjectId))
}

function buildDesignTokens(tokens: StudioPreviewTokens, colorScheme: 'light' | 'dark'): Record<string, string> {
  return getIsolatedAppStyle(tokens ?? undefined, colorScheme) as Record<string, string>
}

export function useAppDesignSandbox({
  appId,
  tokens,
  colorScheme,
  containerRef,
  apiBaseUrl,
}: UseAppDesignSandboxOptions): UseAppDesignSandboxResult {
  const sandboxRef = useRef<AppSandbox | null>(null)
  const [sandbox, setSandbox] = useState<AppSandbox | null>(null)
  const [ready, setReady] = useState(false)
  const sandboxId = useMemo(
    () => appId ? getStudioPreviewSandboxAppId(appId) : null,
    [appId],
  )
  const designTokens = useMemo(
    () => buildDesignTokens(tokens, colorScheme),
    [tokens, colorScheme],
  )
  const latestDesignTokensRef = useRef(designTokens)

  useEffect(() => {
    latestDesignTokensRef.current = designTokens
  }, [designTokens])

  useEffect(() => {
    setReady(false)
    sandboxRef.current?.unmount()
    sandboxRef.current?.destroy()
    sandboxRef.current = null
    setSandbox(null)

    if (!sandboxId) return

    let cancelled = false
    let next: AppSandbox | null = null

    import('@cogo/react-renderer-v2')
      .then((mod) => {
        if (cancelled) return
        if (typeof mod.createAppSandbox !== 'function') {
          throw new Error('createAppSandbox export is unavailable')
        }
        next = mod.createAppSandbox({
          appId: sandboxId,
          designTokens: latestDesignTokensRef.current,
          apiBaseUrl,
          onNavigationAttempt: (url, type) => {
            if (import.meta.env.DEV) {
              console.warn(`[StudioAppSandbox] generated app tried to navigate (${type}): ${url}`)
            }
          },
        })
        sandboxRef.current = next
        setSandbox(next)
      })
      .catch((err) => {
        if (cancelled) return
        console.warn('[StudioAppSandbox] failed to create generated-app sandbox', err)
      })

    return () => {
      cancelled = true
      next?.unmount()
      next?.destroy()
      if (sandboxRef.current === next) {
        sandboxRef.current = null
        setSandbox(null)
        setReady(false)
      }
    }
  // Recreate the sandbox only when the app identity or runtime API base changes.
  // Theme changes are hot-swapped by updateTokens below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sandboxId, apiBaseUrl])

  useLayoutEffect(() => {
    if (!sandbox) return
    const el = containerRef.current
    if (!el) return

    sandbox.mount(el)
    setReady(true)
    return () => {
      setReady(false)
      sandbox.unmount()
    }
  }, [sandbox, containerRef])

  useEffect(() => {
    const active = sandboxRef.current
    if (!active || active.state === 'destroyed') return
    active.updateTokens(designTokens)
  }, [sandbox, designTokens])

  return { sandbox, ready }
}
